import { Typography } from "@suraasa/placebo-ui"
import { LearningItem } from "api/resources/learningItems/types"
import { getCourseVideoPoster } from "components/VideoPlayer/getPoster"
import VideoPlayerV2, { VideoPlayerHandle } from "components/VideoPlayerV2"
import debounce from "lodash/debounce"
import { useContext, useEffect, useRef } from "react"
import { isIOS } from "react-device-detect"
import { useParams } from "react-router-dom"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { LearningModuleContext } from "../context"
import { useUpdateItemStatus } from "../hooks/useUpdateItemStatus"

type Props = {
  item: LearningItem
}

const DEFAULT_LANGUAGE_ID = "default"

const RecordedClass = ({ item }: Props) => {
  const { slug } = useParams() as { slug: string }
  const { enrollments } = useEnrollments()

  const courseName = enrollments?.find(x => x.slug === slug)?.name

  if (item.learningContentType !== "pre_recorded_class") {
    throw new Error(
      "Recorded class component can only be used for recorded class content type"
    )
  }

  const { updateItemStatus } = useUpdateItemStatus(item)

  const {
    // addVideoBookmarkInState,
    // removeVideoBookmarkFromState,
    enableVideoNextButton,
  } = useContext(LearningModuleContext)

  //   const [openNotesDialog, setOpenNotesDialog] = useState(false)

  const videoPlayerRef = useRef<VideoPlayerHandle>(null)

  function handleExitFullScreen() {
    try {
      if (
        (document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement) &&
        document.exitFullscreen
      ) {
        document.exitFullscreen()
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    const videoPlayerRefCurrent = videoPlayerRef.current
    if (!videoPlayerRefCurrent) return

    videoPlayerRefCurrent.addEventListener("ended", handleExitFullScreen)

    return () => {
      videoPlayerRefCurrent.removeEventListener("ended", handleExitFullScreen)
    }
  }, [])

  useEffect(() => {
    if (!videoPlayerRef.current) return

    const player = videoPlayerRef.current

    const onPlay = debounce(() => {
      updateItemStatus()
    }, 100)

    const onTimeUpdate = () => {
      const currentTime = player.currentTime
      const duration = player.duration

      if (currentTime >= duration * 0.9) {
        console.info("> Allow next")
        enableVideoNextButton()
      }
    }

    player.addEventListener("playing", onPlay)
    player.addEventListener("timeupdate", onTimeUpdate)

    return () => {
      player.removeEventListener("playing", onPlay)
      player.removeEventListener("timeupdate", onTimeUpdate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateItemStatus])

  const { title, recording } = item
  if (!recording) throw new Error("Recording url is not defined")

  useEffect(() => {
    getCourseVideoPoster(title, { courseName }).then(result => {
      videoPlayerRef.current?.setPoster(result)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title])

  const sources = [
    {
      language: "English",
      uuid: DEFAULT_LANGUAGE_ID,
      url: recording,
    },
  ]

  return (
    <div className="mb-5">
      <VideoPlayerV2
        ref={videoPlayerRef}
        sources={sources}
        defaultLanguageId={DEFAULT_LANGUAGE_ID}
        autoPlay={!isIOS}
        style={{
          borderRadius: "22px",
          overflow: "hidden",
        }}
      />
      <Typography variant="title3" className="mt-2">
        {title}
      </Typography>
    </div>
  )
}

export default RecordedClass
